import { Box, Grid, Paper, Typography } from '@mui/material'
import { FunctionComponent } from 'react'
import moment from 'moment'
import { FormTextInputDisplay } from '../../form-field-components/new-and-edit/form-text-input'
import { FormDateInputDisplay } from '../../form-field-components/new-and-edit/form-date-input'
import { ICrpFigures } from '../../../../shared/services/crp-report/crp-report.model'

/**
 * Step 4 — the declaration and board sign-off PPN 006 requires.
 *
 * The declaration wording is fixed by the Cabinet Office and comes from the backend, so it is shown
 * read-only here: it lives in exactly one place and cannot drift between the screen and the document.
 */
export const CrpStepSignOff: FunctionComponent<{
  values: Record<string, any>
  figures: ICrpFigures
  onChange: (field: string, value: any) => void
}> = ({ values, figures, onChange }) => {
  const paragraphs = figures.declarationParagraphs
    || (figures.declarationText ? figures.declarationText.split('\n\n') : [])

  return (
    <>
      <Typography variant='h6' sx={{ mb: 1 }}>Declaration and sign off</Typography>
      <Typography variant='body2' sx={{ color: 'text.secondary', mb: 3 }}>
        PPN 006 requires the plan to be signed off at board or director level, and published on your website.
      </Typography>

      <Paper variant='outlined' sx={{ p: 3, mb: 4, backgroundColor: 'background.default' }}>
        <Typography variant='subtitle2' sx={{ mb: 1.5 }}>
          The following declaration will appear in your plan
        </Typography>
        {paragraphs.map((paragraph, index) => (
          <Typography variant='body2' sx={{ color: 'text.secondary', mb: 1 }} key={index}>
            {paragraph}
          </Typography>
        ))}
      </Paper>

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} md={6}>
          <FormTextInputDisplay
            isEditPage={false}
            disabled={false}
            title='Signed on behalf of the supplier by'
            name='signOffName'
            value={values.signOffName || ''}
            onChange={(event: any) => onChange('signOffName', event.target.value)}
            forceOutlined
            regularFieldLabel
            isTopRow
            isBottomRow
            helperText='The name of the director or board member approving the plan.'
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <FormTextInputDisplay
            isEditPage={false}
            disabled={false}
            title='Job title'
            name='signOffTitle'
            value={values.signOffTitle || ''}
            onChange={(event: any) => onChange('signOffTitle', event.target.value)}
            forceOutlined
            regularFieldLabel
            isTopRow
            isBottomRow
          />
        </Grid>
      </Grid>

      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <FormDateInputDisplay
            isEditPage={false}
            disabled={false}
            title='Sign-off date'
            name='signOffDate'
            value={values.signOffDate ? moment(values.signOffDate) : null}
            onChange={(value: any) => onChange('signOffDate', value ? moment(value).format('YYYY-MM-DD') : null)}
            isTopRow
            isBottomRow
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <FormDateInputDisplay
            isEditPage={false}
            disabled={false}
            title='Publication date'
            name='publicationDate'
            value={values.publicationDate ? moment(values.publicationDate) : null}
            onChange={(value: any) => onChange('publicationDate', value ? moment(value).format('YYYY-MM-DD') : null)}
            isTopRow
            isBottomRow
          />
        </Grid>
      </Grid>

      <Box sx={{ mt: 3 }}>
        <Typography variant='caption' sx={{ color: 'text.secondary' }}>
          {figures.disclaimerText}
        </Typography>
      </Box>
    </>
  )
}
